import { DialogComponent } from '@theme/dialog';

/**
 * A dialog that submits a real Shopify "contact" form (native /contact
 * endpoint - no app, no exposed API keys) so the message genuinely reaches
 * the merchant's own inbox, and optionally also subscribes the sender to
 * the store's newsletter via Shopify's native customer/newsletter form.
 *
 * Progressive enhancement: the <form> inside this dialog is a real,
 * server-rendered {% form 'contact' %} that works with JavaScript disabled
 * (full page reload, Shopify handles validation/CSRF natively).
 *
 * IMPORTANT - this component does NOT intercept submission with fetch().
 * It used to (so the dialog could show an inline success state without a
 * page reload), but this store has Shopify's native storefront bot
 * protection (CAPTCHA) enabled, which every form posting to /contact with
 * form_type=contact or form_type=customer is automatically enrolled in.
 * That protection works by listening for the form's real, trusted `submit`
 * event and - when needed - redirecting the shopper through a genuine
 * /challenge page before letting the actual POST through. A fetch()-based
 * submission bypasses that native `submit` event entirely, so the
 * protection never gets a chance to run and Shopify's server unconditionally
 * rejects the request with "Missing CAPTCHA token" (HTTP 400) - which is
 * what previously showed up to shoppers as "Something went wrong - please
 * try again," on every single submission, regardless of what they typed.
 *
 * The fix is to stop fighting the platform: let the form submit natively so
 * Shopify's own bot-protection script (already loaded site-wide) can do its
 * job exactly as it does for every other native form on this store. This
 * component still composes the friendly, human-readable email body (folding
 * fields like "Partner's Name"/"Partner's Email" into one message) and still
 * fires the best-effort newsletter signup - it just writes that composed
 * body into a real hidden field on the form and lets the browser's normal
 * submission (whether immediate, or after the shopper clears a CAPTCHA
 * challenge) carry it, instead of building its own request. See
 * #reopenAfterServerPostback() for how the dialog reflects the result of
 * that submission after the page reloads.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 *
 * @extends DialogComponent
 */
export class InquiryDialog extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', this.handleSubmit);

    // DialogComponent's own closeDialog is set as an instance field (not a
    // prototype method), so it can't be reached via `super.closeDialog()`
    // from a subclass. Resetting on the dialog's native "close" event
    // (fired once the <dialog> actually closes, whichever code closed it)
    // avoids depending on that internal implementation detail entirely.
    const dialog = this.querySelector('dialog');
    if (dialog) dialog.addEventListener('close', this.#handleDialogClose);

    this.#setUpCharCounters();
    this.#reopenAfterServerPostback();
  }

  /**
   * Wires up a live "n/max characters" counter for any textarea carrying a
   * `data-char-counter="<id>"` attribute pointing at its counter element's id.
   */
  #setUpCharCounters() {
    const textareas = this.querySelectorAll('textarea[data-char-counter]');

    textareas.forEach((textarea) => {
      if (!(textarea instanceof HTMLTextAreaElement)) return;

      const counterId = textarea.dataset.charCounter;
      const counter = counterId ? this.querySelector(`#${CSS.escape(counterId)}`) : null;
      if (!(counter instanceof HTMLElement)) return;

      const update = () => {
        counter.textContent = `${textarea.value.length}/${textarea.maxLength}`;
      };

      textarea.addEventListener('input', update);
      update();
    });
  }

  /**
   * After a real submission of this form completes - either immediately, or
   * after the shopper clears Shopify's CAPTCHA challenge - the browser lands
   * back on this same page at `?contact_posted=true#<dialog id>`. The
   * section is re-rendered server-side with `form.errors` /
   * `form.posted_successfully?` already reflected in `[data-inquiry-error]`
   * / `[data-inquiry-success]` (see hint-and-opinion.liquid), but the
   * <dialog> itself always starts closed on a fresh page load, and would
   * otherwise leave that result invisible. Reopen it, and mirror the old
   * inline-success UI (hide the form, reveal the success message) whenever
   * this particular form has server-rendered feedback to show.
   */
  #reopenAfterServerPostback() {
    const errorEl = this.querySelector('[data-inquiry-error]');
    const successEl = this.querySelector('[data-inquiry-success]');
    const hasError = errorEl instanceof HTMLElement && !errorEl.hidden;
    const hasSuccess = successEl instanceof HTMLElement && !successEl.hidden;

    if (!hasError && !hasSuccess) return;

    this.showDialog();

    if (hasSuccess) {
      const form = this.querySelector('form');
      if (form instanceof HTMLFormElement) form.hidden = true;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const form = this.querySelector('form');
    if (form) form.removeEventListener('submit', this.handleSubmit);

    const dialog = this.querySelector('dialog');
    if (dialog) dialog.removeEventListener('close', this.#handleDialogClose);
  }

  /**
   * Runs on the form's real `submit` event. Composes the friendly message
   * body and writes it into a real hidden `contact[body]` field, best-effort
   * fires the newsletter signup, and then gets out of the way - it
   * deliberately does NOT call event.preventDefault() (other than for
   * client-side validation) or submit via fetch(), so the browser's normal
   * submission - and this store's native CAPTCHA protection - run exactly as
   * they would for any other form. See the class doc comment above for why.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);

    if (!(form instanceof HTMLFormElement)) return;

    if (!form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);

    const composeBodyEl = form.querySelector('[data-compose-body]');
    const composeBody = composeBodyEl instanceof HTMLElement ? composeBodyEl.dataset.composeBody : undefined;
    if (composeBody) {
      let body = this.#composeBody(formData, composeBody);

      const sendCopyCheckbox = form.querySelector('[name="contact[Send copy to sender]"]');
      if (sendCopyCheckbox instanceof HTMLInputElement && sendCopyCheckbox.checked) {
        body += '\n\nThe sender asked to receive a copy of this email as well - please CC them when you reply.';
      }

      // Write the composed body into a real hidden field so it rides along
      // with whatever submission actually goes out (this one, or the
      // resubmission Shopify's own CAPTCHA flow performs) - rather than a
      // FormData object we build and send ourselves.
      let bodyField = form.elements.namedItem('contact[body]');
      if (!(bodyField instanceof HTMLInputElement)) {
        bodyField = document.createElement('input');
        bodyField.type = 'hidden';
        bodyField.name = 'contact[body]';
        form.append(bodyField);
      }
      bodyField.value = body;
    }

    const subscribeCheckbox = form.querySelector('[data-subscribe-checkbox]');
    if (subscribeCheckbox instanceof HTMLInputElement && subscribeCheckbox.checked) {
      const email = String(formData.get('contact[email]') || '').trim();

      if (email) {
        const subscribeData = new FormData();
        subscribeData.set('form_type', 'customer');
        subscribeData.set('utf8', '✓');
        subscribeData.set('contact[tags]', 'newsletter');
        subscribeData.set('contact[email]', email);

        // Best-effort, fire-and-forget: never block or interfere with the
        // real form submission below, and never surface its own errors to
        // the shopper (matches the previous behaviour).
        fetch('/contact#ContactFooter', { method: 'POST', body: subscribeData }).catch((subscribeError) =>
          console.error(subscribeError)
        );
      }
    }

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    // No event.preventDefault() here: the browser now submits this form for
    // real, letting Shopify's own bot-protection script step in if needed.
  };

  /**
   * Builds a single readable message body from a template string containing
   * `%%field_name%%` placeholders (matching this form's own field names) and
   * literal `\n` line breaks, so every detail the shopper entered reaches
   * the merchant's inbox inside the one guaranteed-visible field, regardless
   * of how the store's contact notification template handles extra custom
   * fields.
   *
   * @param {FormData} formData
   * @param {string} template
   * @returns {string}
   */
  #composeBody(formData, template) {
    const withValues = template.replace(/%%(.+?)%%/g, (_match, key) => {
      const value = formData.get(key.trim());
      return value ? String(value).trim() : '';
    });

    return withValues.replace(/\\n/g, '\n');
  }

  /**
   * Resets the dialog back to its initial (pre-submit) state once it has
   * actually closed (native <dialog> "close" event), so reopening it never
   * shows stale success text - regardless of what closed it (the close
   * button, Escape, or an outside click, all handled by DialogComponent).
   */
  #handleDialogClose = () => {
    this.#resetForm();
  };

  #resetForm() {
    const form = this.querySelector('form');
    const errorEl = this.querySelector('[data-inquiry-error]');
    const successEl = this.querySelector('[data-inquiry-success]');

    if (form instanceof HTMLFormElement) {
      form.reset();
      form.hidden = false;
    }
    if (errorEl instanceof HTMLElement) errorEl.hidden = true;
    if (successEl instanceof HTMLElement) successEl.hidden = true;
  }
}

if (!customElements.get('inquiry-dialog')) {
  customElements.define('inquiry-dialog', InquiryDialog);
}
